<template>
    <div class="main">
        <div class="admin-section">
            <div class="container">
                <div class="row">
                    <h1 class="section-heading col-12">Edit "Members of the board"</h1>
                    <b-table id="members-table" 
                             :busy="loading"
                             :items="sortedItems"
                             :fields="fields">
                        <template #cell(commands)="row">
                            <b-button size="sm" @click="deleteItem(row.item)" class="mr-2 delete-button">
                                Delete
                            </b-button>
                            <b-button size="sm" @click="editItem(row.item)" class="mr-2 edit-button">
                                Edit
                            </b-button>
                        </template>
                        <template #table-busy>
                            <div class="text-center text-danger my-2">
                                <b-spinner class="align-middle"></b-spinner>
                                <strong>Loading...</strong>
                            </div>
                        </template>
                    </b-table>
                </div>
                <div class="buttons-container float-right">
                    <b-button @click="onAddNew" class="submit-button">Add new member</b-button>
                </div>
            </div>
        </div>
        <b-modal ref="editPopup" 
                 :no-close-on-backdrop="true"
                 title="Edit/Create member"
                 @close="onPopupClose"
                 @ok="onEditSave"
                 @cancel="onPopupCancel"
                 size="lg">
            <b-overlay :show="updating">
                <form v-if="item">
                    <b-form-group label="Name:"
                                    label-for="name"
                                    invalid-feedback="Member name is required. Enter at least 2 letters."
                                    :state="nameState">
                        <b-form-input id="name"
                                    v-model="item.name"
                                    required
                                    :state="nameState"
                                    trim></b-form-input>
                    </b-form-group>
                    <b-form-group label="Title:"
                                    label-for="title"
                                    invalid-feedback="Member title is required. Enter at least 2 letters."
                                    :state="titleState">
                        <b-form-input id="title"
                                    v-model="item.title"
                                    required
                                    :state="titleState"
                                    trim></b-form-input>
                    </b-form-group>
                    <b-form-group label="Presentation:"
                                    label-for="description"
                                    invalid-feedback="Member description is required. Enter at least 2 letters."
                                    :state="descriptionState">
                        <b-form-textarea id="description"
                                    v-model="item.description"
                                    required
                                    :state="descriptionState"
                                    rows="5"
                                    max-rows="10"
                                    class="description-field"
                                    trim></b-form-textarea>
                    </b-form-group>
                    <b-form-group label="Order number:"
                                    label-for="orderId"
                                    invalid-feedback="Order number should be greater than 0."
                                    :state="orderState">
                        <b-form-input id="orderId"
                                    v-model="item.orderId"
                                    required
                                    :state="orderState"
                                    type="number"
                                    trim></b-form-input>
                    </b-form-group>
                    <b-form-group label="Visible on website:"
                                    label-for="visible">
                        <b-form-checkbox id="visible"
                                    v-model="item.visible"
                                    type="checkbox"></b-form-checkbox>
                    </b-form-group>
                </form>
            </b-overlay>
        </b-modal>

        <b-modal ref="confirmDelete" 
                 :no-close-on-backdrop="true"
                 title="Delete member from list"
                 @close="onPopupClose"
                 @ok="onConfirmDelete"
                 @cancel="onPopupCancel">
            <p v-if="item">
                Are you sure you want to delete <strong>"{{ item.name }}"</strong> from database?
            </p>
        </b-modal>
    </div>
</template>

<script>
import { doc, deleteDoc, updateDoc, setDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";

export default {
    name: "EditMembers",
    data() {
        return {
            inEditing: false,
            updating: false,
            fields: [
                { key: 'orderId', label: 'Order' },
                'name',
                'title',
                'visible',
                'commands'
            ],
            item: null
        }
    },
    computed: {
        loading() {
            return this.inEditing || !this.items || this.items.length === 0;
        },
        items() {
            return this.$store.state.members;
        },
        rows() {
            return this.items.length;
        },
        sortedItems() {
            return this.items.slice().sort((a, b) => { return a.orderId - b.orderId });
        },
        nameState() {
            return this.item.name.length > 1 ? true : false;
        },
        titleState() {
            return this.item.title.length > 1 ? true : false;
        },
        descriptionState() {
            return this.item.description.length > 1 ? true : false;
        },
        orderState() {
            return this.item.orderId > 0 ? true : false;
        },
    },
    methods: {
        deleteItem(item) {
            if (item && this.confirmDelete) {
                this.item = item;
                this.confirmDelete.show();
            }
        },
        editItem(item) {
            if (item && this.editPopup) {
                this.item = JSON.parse(JSON.stringify(item));
                this.editPopup.show();
            }
        },
        async onConfirmDelete() {
            let index;

            if (this.item) {
                try {
                    this.inEditing = true;
                    await deleteDoc(doc(db, "members", this.item.id));

                    index = this.items.findIndex(item => item.id === this.item.id);
                    this.items.splice(index, 1);

                    alert(this.item.name + " successfully deleted.");
                    this.item = null;
                } catch {
                    alert("Member delete failed.");
                } finally {
                    this.inEditing = false;
                }
            }
        },
        async onEditSave(e) {
            e.preventDefault();

            if (this.item && this.nameState && this.titleState && this.descriptionState && this.orderState) {
                let id = this.item.id,
                    docRef, index, clone;

                this.updating = true;
                this.item.orderId = Number(this.item.orderId);

                if (id) {
                    // update item
                    try {
                        clone = Object.assign({}, this.item);
                        delete clone.id;

                        docRef = doc(db, "members", id);
                        await updateDoc(docRef, clone);

                        index = this.items.findIndex(item => item.id === id);
                        this.$set(this.items, index, JSON.parse(JSON.stringify(this.item)));

                        alert(this.item.name + " successfully updated.");
                        this.item = null;
                        this.editPopup.hide();
                    } catch {
                        alert("Member update failed.");
                    } finally {
                        this.updating = false;
                    }
                } else {
                    // create item
                    try {
                        docRef = doc(db, "members", this.item.name);

                        await setDoc(docRef, this.item);

                        this.item.id = docRef.id;

                        this.items.push(JSON.parse(JSON.stringify(this.item)));

                        alert(this.item.name + " successfully created.");
                        this.item = null;
                        this.editPopup.hide();
                    } catch {
                        alert("Member create failed.");
                    } finally {
                        this.updating = false;
                    }
                }
            } else {
                alert('Name, Title, Description and Order number should be provided for each member.');
            }
        },
        onPopupClose() {
            this.item = null;
        },
        onPopupCancel() {
            this.item = null;
        },
        onAddNew() {
            this.editItem({
                orderId: 0, 
                name: '',
                title: '', 
                description: '', 
                visible: true
            });
        }
    },
    mounted() {
        if (!auth.currentUser) {
            this.$router.push('/admin');
        }

        this.editPopup = this.$refs['editPopup'];
        this.confirmDelete = this.$refs['confirmDelete'];
    }
}
</script>

<style scoped>
 .description-field {
     overflow-y: auto !important;
 }
</style>